/**
 * Interpretación de una línea de log para verla formateada.
 *
 * Los logs de la **app** son una línea por registro en JSON (pino); los del
 * **panel** son texto plano (`2026-10-15T12:34:42: INFO: …`) y las continuaciones
 * (stack traces) son líneas sueltas. Aquí se intenta leer el JSON y, cuando lo
 * es, se **ordena** para que el detalle se lea igual siempre: primero los campos
 * que identifican el registro (hora, nivel, logger y servicio), después el
 * mensaje y el contexto, y al final el resto por orden alfabético.
 */

/** Claves que se muestran primero, en este orden (las que no están no aparecen). */
const ORDEN_PREFERIDO = [
  'time',
  'timestamp',
  'date',
  'level',
  'nivel',
  'logger_name',
  'logger',
  'app_code',
  'service_name',
  'service_type',
  'service_version',
  'environment',
  'hostname',
  'pid',
  'message',
  'msg',
  'context',
  'stack',
];

/**
 * Nombre comparable de una clave: sin mayúsculas ni separadores.
 *
 * Los registros mezclan criterios (`app_code`, `app-code`, `logger_name`), así que
 * la clave se compara normalizada para que las dos formas caigan en el mismo
 * puesto del orden preferido.
 */
function claveNormalizada(clave: string): string {
  return clave.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** El orden preferido ya normalizado, para comparar de una vez. */
const ORDEN_PREFERIDO_NORMALIZADO = ORDEN_PREFERIDO.map(claveNormalizada);

/** Posición de una clave en el orden preferido (`null` si no está en la lista). */
function posicionPreferida(clave: string): number | null {
  const indice = ORDEN_PREFERIDO_NORMALIZADO.indexOf(claveNormalizada(clave));

  return indice === -1 ? null : indice;
}

/** `true` cuando el valor es un objeto plano (no un array ni `null`). */
export function esObjetoPlano(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/** El objeto con sus claves ordenadas (preferidas primero, el resto alfabético). */
function ordenarClaves(objeto: Record<string, unknown>): Record<string, unknown> {
  const claves = Object.keys(objeto).sort((a, b) => {
    const posicionA = posicionPreferida(a);
    const posicionB = posicionPreferida(b);

    if (posicionA !== null && posicionB !== null) return posicionA - posicionB;
    if (posicionA !== null) return -1;
    if (posicionB !== null) return 1;

    const comparacion = a.toLowerCase().localeCompare(b.toLowerCase());

    // `localeCompare` puede empatar dos claves que sólo difieren en mayúsculas:
    // el desempate con el texto crudo mantiene el orden estable.
    return comparacion !== 0 ? comparacion : a.localeCompare(b);
  });

  const ordenado: Record<string, unknown> = {};

  for (const clave of claves) ordenado[clave] = ordenarValor(objeto[clave]);

  return ordenado;
}

/** Aplica el orden a los objetos anidados; los arrays conservan su orden. */
function ordenarValor(valor: unknown): unknown {
  if (Array.isArray(valor)) return valor.map(ordenarValor);
  if (esObjetoPlano(valor)) return ordenarClaves(valor);

  return valor;
}

/**
 * Lee la línea como registro JSON.
 *
 * Devuelve `null` cuando no es JSON o cuando es un JSON que no es un objeto
 * (una cadena o un array suelto): en ese caso el detalle muestra el texto tal cual.
 */
export function parsearRegistro(texto: string): Record<string, unknown> | null {
  const limpio = texto.trim();

  if (!limpio.startsWith('{')) return null;

  try {
    const valor = JSON.parse(limpio) as unknown;

    return esObjetoPlano(valor) ? valor : null;
  } catch {
    return null;
  }
}

/** Registro JSON en texto, ya ordenado e indentado (el formato que pide el usuario). */
export function formatearRegistro(registro: Record<string, unknown>): string {
  try {
    return JSON.stringify(ordenarClaves(registro), null, 2);
  } catch {
    // Un valor con referencias circulares o `BigInt` rompería el render.
    return `${JSON.stringify(registro) ?? String(registro)}`;
  }
}

/** Texto de un campo del registro cuando es una cadena con contenido. */
export function campoTexto(registro: Record<string, unknown> | null, claves: string[]): string | null {
  if (!registro) return null;

  for (const clave of claves) {
    const valor = registro[clave];

    if (typeof valor === 'string' && valor.trim() !== '') return valor;
    if (typeof valor === 'number' && Number.isFinite(valor)) return String(valor);
  }

  return null;
}
